import React from 'react';
import {useUnrealUIContext} from '../../../shared/context/componentContext';
import MatLink, {LinkProps as MatLinkProps} from '@material-ui/core/Link/Link';
import {determineExternal, stopFocusOnClick, useAnalyticsCallback} from 'epic-ue-shared';
import {generateRoutePath, getLocale} from '@epic-core/common';
import {RichTextSpan} from '../../elements/text/RichTextSpan';
import {AccentColorOptions, useCurrentAccentColor} from 'epic-ue-theme';
import styled from 'styled-components';

const Styled = styled.span`
    && {
        .ue-outlined:focus-visible {
            outline: 1px solid ${(p) => p.theme.palette.misc.outline};
            outline-offset: 2px;
            & > div {
                outline: 1px solid ${(p) => p.theme.palette.misc.outline};
                outline-offset: 2px;
            }
            & > .heading {
                outline: 1px solid ${(p) => p.theme.palette.misc.outline};
                outline-offset: 2px;
                width: fit-content;
            }
        }
        a.ue-outlined.Mui-focusVisible {
            background: unset;
        }
        &:active {
            text-decoration: none;
        }
        a {
            color: inherit;
            text-decoration: none;
        }
    }
`;

interface RouteComponentDefaultProps {
    children?: React.ReactNode;
}

const useUnrealContextRouteComponent = (): React.ForwardRefExoticComponent<
    React.RefAttributes<unknown>
> => {
    const {RouteComponent} = useUnrealUIContext();

    const UnrealContextRouteComponentWithRef = React.forwardRef(
        (props: RouteComponentDefaultProps, ref): JSX.Element => {
            if (!RouteComponent) {
                return (
                    <a {...props} className="ue-link-wrap">
                        {props.children}
                    </a>
                );
            }

            return <RouteComponent {...props} ref={ref} />;
        }
    );
    UnrealContextRouteComponentWithRef.displayName = 'UnrealContextRouteComponentWithRef';

    return UnrealContextRouteComponentWithRef;
};

interface LinkProps {
    component?: React.ElementType<any> | 'a';
}

const replaceLocaleTemplate = (href?: string): string | undefined => {
    if (href && href.indexOf(':locale') > -1) {
        const locale = getLocale();
        return href.replace(/:locale/ig, locale);
    }
    return href;
};

export const UeLink = (props: LinkProps & MatLinkProps): JSX.Element | null => {
    const {onClick, id} = props;
    const href = replaceLocaleTemplate(props.href);

    const clickCallback = useAnalyticsCallback(onClick, id, href);
    const routeComponent = useUnrealContextRouteComponent();

    const {name} = useCurrentAccentColor();
    const currentAccentColor = name ? (name as AccentColorOptions) : 'sky';

    if (!props.children) return null;

    const {isExternal, sameOrigin, isMailTo, isLauncher} = determineExternal(href);

    let component: React.ElementType<any> = props.component || 'a';

    let rel = props.rel;
    let target = props.target;
    let to;

    const content =
        typeof props.children === 'string' ? (
            <RichTextSpan
                accentColor={currentAccentColor}
                className="ue-link-wrap-rich-text"
                richText={props.children}
            />
        ) : (
            props.children
        );

    if (isExternal && !isMailTo && !isLauncher && !sameOrigin) {
        rel = 'noopener noreferrer nofollow';
        target = '_blank';
    } else if (!isExternal && !isLauncher && href) {
        if (!isMailTo) component = props.component || routeComponent || 'a';
        to = generateRoutePath(href);
    } else if (!href && onClick) {
        return (
            <Styled className={'ue-link-wrap-span'}>
                <span
                    role="button"
                    tabIndex={0}
                    className="ue-outlined ue-onclick-wrap"
                    onClick={clickCallback}
                    onMouseDown={stopFocusOnClick}
                >
                    {content}
                </span>
            </Styled>
        );
    } else if (href && !onClick) {
        to = href;
    } else if (!href) {
        return <span className="ue-non-link-wrap">{content}</span>;
    }

    return (
        <Styled className={'ue-link-wrap-span'}>
            <MatLink
                {...props}
                component={component}
                to={to}
                rel={rel}
                target={target}
                underline="none"
                className="ue-outlined ue-link-wrap"
                onClick={clickCallback}
                onMouseDown={stopFocusOnClick}
            >
                {content}
            </MatLink>
        </Styled>
    );
};
